/**
 * Verify Email Page
 * 
 * Handles the callback from Better Auth after user clicks email verification link.
 * - On success: Shows success message and redirects to dashboard
 * - On error: Shows error message with option to resend
 */

import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, XCircle, Mail, ArrowRight, RefreshCw } from 'lucide-react';
import { sendVerificationEmail, useSession } from '@/lib/auth-client';
import toast from 'react-hot-toast';

export function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: session } = useSession();
  const error = searchParams.get('error');
  
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const [shouldRedirect] = useState(!error);

  // Auto-redirect to dashboard on success
  useEffect(() => {
    if (shouldRedirect && !error) {
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            navigate('/dashboard');
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [shouldRedirect, error, navigate]);

  const handleResend = async () => {
    if (!session?.user?.email) {
      toast.error('Please log in to resend verification email');
      navigate('/login');
      return;
    }

    setIsResending(true);
    try {
      const { error: resendError } = await sendVerificationEmail({
        email: session.user.email,
        callbackURL: '/verify-email',
      });

      if (resendError) {
        toast.error(resendError.message || 'Failed to send verification email');
      } else {
        toast.success('Verification email sent! Check your inbox.');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to send verification email');
    } finally {
      setIsResending(false);
    }
  };

  // Error state - invalid or expired token
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            <div className="text-center">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-red-100 mb-4">
                <XCircle className="h-10 w-10 text-red-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Verification Failed
              </h2>
              <p className="text-gray-600 mb-6">
                {error === 'invalid_token' 
                  ? 'This verification link is invalid or has expired.'
                  : `Verification error: ${error}`
                }
              </p>
              
              <div className="space-y-4">
                {session?.user && (
                  <button
                    onClick={handleResend}
                    disabled={isResending}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                  >
                    {isResending ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Mail className="w-4 h-4" />
                        Resend Verification Email
                      </>
                    )}
                  </button>
                )}
                
                <Link
                  to="/dashboard"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Go to Dashboard
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Email Verified!
            </h2>
            <p className="text-gray-600 mb-6">
              Your email address has been successfully verified.
            </p>
            
            <p className="text-sm text-gray-500 mb-4">
              Redirecting to dashboard in {countdown} seconds...
            </p>
            
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Go to Dashboard Now
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VerifyEmailPage;
